// Team (simple-mode S3 3.1, D33) over the existing gateway routes:
//
//   GET    /workspaces/{id}/members                      any member (email/name per D39)
//   PATCH  /workspaces/{id}/members/{user_id}/role       manage_roles   {role}
//   DELETE /workspaces/{id}/members/{user_id}            remove_member
//   GET    /workspaces/{id}/invites                      invite_member
//   POST   /workspaces/{id}/invites                      invite_member + members.max  {email, role}
//   DELETE /workspaces/{id}/invites/{invite_id}          invite_member
//
// Every control is gated by the session's server-sourced permission list (useCan,
// rbac-restore D52); the gateway still enforces each route and a 403 is shown, not hidden.
// The owner row has no role select and no remove: account basics D39 makes the gateway
// refuse both (409); ownership moves only through Workspace → Transfer.
// Seats (members.max) = members + pending invites, the same count the gateway uses.
import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiFetch } from '../api/client'
import { errorMessage } from '../api/errors'
import { qk } from '../api/queries'
import type { Invite, InviteCreated, InviteRole, InvitesResponse, Member, MembersResponse, Role } from '../api/types'
import { useSession } from '../context/SessionContext'
import { isOwnerRole, useCan } from '../rbac'
import { planDenialMessage, useCapacity, useEntitlements } from '../entitlements'
import { PlanNote } from '../entitlements/PlanNote'
import { ConfirmAction } from '../components/ConfirmAction'
import { CopyButton } from '../components/CopyButton'
import { formatDate, memberName } from '../components/format'
import { Badge, PageHeader, QueryState } from '../components/ui'

const ROLE_LABEL: Record<Role, string> = { owner: 'Owner', admin: 'Admin', member: 'Member', viewer: 'Viewer' }
// Roles a role change or an invite can grant: never owner (D39; transfer is the only path).
const GRANTABLE: InviteRole[] = ['admin', 'member', 'viewer']

function failText(err: unknown): string {
  return planDenialMessage(err) ?? errorMessage(err)
}

function RoleCell({ member, canManage }: { member: Member; canManage: boolean }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const name = memberName(member)
  const change = useMutation({
    mutationFn: (role: InviteRole) =>
      apiFetch<Member>(`/workspaces/${workspaceId}/members/${member.user_id}/role`, {
        method: 'PATCH',
        body: JSON.stringify({ role }),
      }),
    // Update just this row from the server's answer; on failure the row keeps its role.
    onSuccess: (row) =>
      qc.setQueryData<MembersResponse>(qk.members(workspaceId), (old) => ({
        ...old,
        members: (old?.members ?? []).map((m) => (m.user_id === member.user_id ? { ...m, role: row?.role ?? m.role } : m)),
      })),
  })

  if (!canManage || isOwnerRole(member.role)) {
    return <Badge tone={isOwnerRole(member.role) ? 'info' : 'neutral'}>{ROLE_LABEL[member.role] ?? member.role}</Badge>
  }
  return (
    <div className="field field--compact team-role">
      <select
        aria-label={`Role for ${name}`}
        value={member.role}
        disabled={change.isPending}
        aria-busy={change.isPending}
        onChange={(e) => {
          change.reset()
          change.mutate(e.target.value as InviteRole)
        }}
      >
        {GRANTABLE.map((r) => (
          <option key={r} value={r}>{ROLE_LABEL[r]}</option>
        ))}
      </select>
      {change.error ? (
        <span className="form-error" role="alert">Couldn’t change {name}’s role. {errorMessage(change.error)}</span>
      ) : null}
    </div>
  )
}

function InviteForm({ seatsGate }: { seatsGate: ReturnType<typeof useCapacity> }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<InviteRole>('member')
  const create = useMutation({
    mutationFn: (body: { email: string; role: InviteRole }) =>
      apiFetch<InviteCreated>(`/workspaces/${workspaceId}/invites`, { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setEmail('')
      void qc.invalidateQueries({ queryKey: qk.invites(workspaceId) })
      void qc.invalidateQueries({ queryKey: qk.entitlements(workspaceId) })
    },
  })
  const created = create.data
  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const value = email.trim()
    if (!value || create.isPending || !seatsGate.allowed) return
    create.mutate({ email: value, role })
  }

  return (
    <>
      <form className="team-invite" aria-label="Invite a member" onSubmit={onSubmit}>
        <div className="field">
          <label htmlFor="invite-email">Email</label>
          <input
            id="invite-email"
            type="email"
            autoComplete="off"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            maxLength={254}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="invite-role">Role</label>
          <select id="invite-role" value={role} onChange={(e) => setRole(e.target.value as InviteRole)}>
            {GRANTABLE.map((r) => (
              <option key={r} value={r}>{ROLE_LABEL[r]}</option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="btn btn--primary"
          disabled={!seatsGate.allowed || create.isPending || !email.trim()}
          aria-busy={create.isPending}
        >
          {create.isPending ? 'Inviting…' : 'Send invite'}
        </button>
      </form>
      <PlanNote gate={seatsGate} />
      {create.error ? <p className="form-error" role="alert">{failText(create.error)}</p> : null}
      {created && (
        <div className="secret-once" role="status" aria-label="Invite created">
          <p className="warn-note">
            {created.delivery === 'supabase_invite'
              ? `We emailed ${created.invite.email} an invite. You can also share this link; it is shown once.`
              : `${created.invite.email} will see the invite when they sign in. You can also share this link; it is shown once.`}
          </p>
          <pre aria-label="Invite link">{created.invite_url}</pre>
          <div className="row-gap">
            <CopyButton value={created.invite_url} label="Copy invite link" />
            <button type="button" className="btn btn--sm" onClick={() => create.reset()}>Done</button>
          </div>
        </div>
      )}
    </>
  )
}

function PendingInvites({ invites }: { invites: Invite[] }) {
  const { workspaceId } = useSession()
  const qc = useQueryClient()
  const [revoking, setRevoking] = useState<Invite | null>(null)
  const revoke = useMutation({
    mutationFn: (invite: Invite) => apiFetch(`/workspaces/${workspaceId}/invites/${invite.id}`, { method: 'DELETE' }),
    onSuccess: (_d, invite) => {
      qc.setQueryData<InvitesResponse>(qk.invites(workspaceId), (old) => ({
        invites: (old?.invites ?? []).filter((i) => i.id !== invite.id),
      }))
      void qc.invalidateQueries({ queryKey: qk.entitlements(workspaceId) })
      setRevoking(null)
    },
  })
  if (invites.length === 0) return <p className="muted">No pending invites.</p>
  return (
    <>
      <div className="table-wrap">
        <table className="table" aria-label="Pending invites">
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Expires</th>
              <th><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {invites.map((i) => (
              <tr key={i.id}>
                <td className="cell-strong">{i.email}</td>
                <td>{ROLE_LABEL[i.role]}</td>
                <td className="cell-sub">{formatDate(i.expires_at)}</td>
                <td className="cell-actions">
                  <button
                    type="button"
                    className="btn btn--sm btn--ghost-danger"
                    aria-label={`Revoke invite for ${i.email}`}
                    onClick={() => {
                      revoke.reset()
                      setRevoking(i)
                    }}
                  >
                    Revoke
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {revoking && (
        <ConfirmAction
          title="Revoke invite"
          confirmLabel="Revoke"
          pendingLabel="Revoking…"
          pending={revoke.isPending}
          error={revoke.error ? errorMessage(revoke.error) : undefined}
          onConfirm={() => revoke.mutate(revoking)}
          onClose={() => setRevoking(null)}
        >
          <p>The invite link for <strong>{revoking.email}</strong> stops working immediately.</p>
        </ConfirmAction>
      )}
    </>
  )
}

export function TeamView() {
  const { workspaceId, session } = useSession()
  const qc = useQueryClient()
  const canManageRoles = useCan('manage_roles')
  const canRemove = useCan('remove_member')
  const canInvite = useCan('invite_member')
  const [removing, setRemoving] = useState<Member | null>(null)

  const members = useQuery({
    queryKey: qk.members(workspaceId),
    queryFn: () => apiFetch<MembersResponse>(`/workspaces/${workspaceId}/members`),
    enabled: !!workspaceId,
  })
  const invites = useQuery({
    queryKey: qk.invites(workspaceId),
    queryFn: () => apiFetch<InvitesResponse>(`/workspaces/${workspaceId}/invites`),
    enabled: !!workspaceId && canInvite,
  })
  // Seats = members + live pending invites. GET /entitlements reports that exact count
  // (`usage.seats`, the number the invite routes check). A gateway older than S4 lacks it:
  // then count members + the pending list here, and keep the gate closed until the list
  // loads (fail closed).
  const ent = useEntitlements().data
  const serverSeats = typeof ent?.usage?.seats === 'number'
  const seats = useCapacity('members.max', serverSeats ? 'seats' : 'members', serverSeats ? 0 : invites.data?.invites.length ?? 0)
  const seatsGate =
    serverSeats || invites.data ? seats : { ...seats, allowed: false, loading: invites.isLoading || seats.loading }

  const remove = useMutation({
    mutationFn: (m: Member) => apiFetch(`/workspaces/${workspaceId}/members/${m.user_id}`, { method: 'DELETE' }),
    onSuccess: (_d, m) => {
      qc.setQueryData<MembersResponse>(qk.members(workspaceId), (old) => ({
        ...old,
        members: (old?.members ?? []).filter((x) => x.user_id !== m.user_id),
      }))
      void qc.invalidateQueries({ queryKey: qk.entitlements(workspaceId) })
      setRemoving(null)
    },
  })

  const list = members.data?.members ?? []
  return (
    <div className="page">
      <PageHeader
        title="Team"
        count={members.data ? list.length : undefined}
        description={
          <>
            Who is in this workspace and what each role can do.{' '}
            <Link to="/permissions">See every role’s permissions</Link>.
          </>
        }
      />
      <QueryState isLoading={members.isLoading} error={members.error} what="this workspace’s members" onRetry={() => members.refetch()}>
        <div className="table-wrap">
          <table className="table" aria-label="Members">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                {canRemove && <th><span className="sr-only">Actions</span></th>}
              </tr>
            </thead>
            <tbody>
              {list.map((m) => {
                const name = memberName(m)
                const you = m.user_id === session?.user.id
                return (
                  <tr key={m.user_id}>
                    <td className="cell-strong">
                      {name}
                      {you && <Badge tone="info">You</Badge>}
                    </td>
                    <td className="cell-sub">{m.email ?? '—'}</td>
                    <td><RoleCell member={m} canManage={canManageRoles} /></td>
                    <td className="cell-sub">{formatDate(m.joined_at)}</td>
                    {canRemove && (
                      <td className="cell-actions">
                        {!isOwnerRole(m.role) && (
                          <button
                            type="button"
                            className="btn btn--sm btn--ghost-danger"
                            aria-label={`Remove ${name}`}
                            onClick={() => {
                              remove.reset()
                              setRemoving(m)
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {!canManageRoles && !canRemove && !canInvite && (
          <p className="muted" role="note">Only a workspace owner or admin can change roles, remove members or invite people.</p>
        )}
      </QueryState>

      {canInvite && (
        <section className="card-section" aria-labelledby="invite-title">
          <h2 id="invite-title" className="section-title">Invite people</h2>
          {serverSeats && seats.used !== undefined && (
            <p className="muted" data-testid="seat-count">
              {seats.limit === null
                ? `${seats.used} seat${seats.used === 1 ? '' : 's'} used (members and pending invites).`
                : `${seats.used} of ${seats.limit} seats used (members and pending invites).`}
            </p>
          )}
          <InviteForm seatsGate={seatsGate} />
          <h3 className="section-subtitle">Pending invites</h3>
          <QueryState isLoading={invites.isLoading} error={invites.error} what="pending invites" onRetry={() => invites.refetch()}>
            <PendingInvites invites={invites.data?.invites ?? []} />
          </QueryState>
        </section>
      )}

      {removing && (
        <ConfirmAction
          title="Remove member"
          confirmLabel="Remove"
          pendingLabel="Removing…"
          pending={remove.isPending}
          error={remove.error ? errorMessage(remove.error) : undefined}
          onConfirm={() => remove.mutate(removing)}
          onClose={() => setRemoving(null)}
        >
          <p>
            <strong>{memberName(removing)}</strong> loses access to this workspace now. Their connected accounts and
            triggers in this workspace are deleted too.
          </p>
        </ConfirmAction>
      )}
    </div>
  )
}
